import React, { useState, useEffect } from "react";
import { Globe, Shield, Loader, RefreshCw, AlertTriangle, Trash2, Plus, Building2, FileText, Key } from "lucide-react";
import { SecretsTab, SurfaceTab, TAB_STYLE } from "./ConnectorsConfig";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import { useHasLicense } from "../contexts/LicenseContext";
import { useRBAC } from "../hooks/useRBAC";
import DarkWebEnrollDealModal from "./DarkWebEnrollDealModal";
import DarkWebEnrollBusinessUnitModal from "./DarkWebEnrollBusinessUnitModal";
import DarkWebEnrollVendorModal from "./DarkWebEnrollVendorModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { useModuleAvailability } from "../hooks/useModuleAvailability";

const DarkWebDomainConfig = () => {
  const [activeTab, setActiveTab] = useState("monitoring");
  // Secrets / Surface tabs follow their own modules (this page is guarded on darkWebMonitoring only,
  // so on Restricted it stays while those two are unavailable).
  const { isModuleAvailable } = useModuleAvailability();
  const tabs = [
    { id: "monitoring", label: "Live Monitoring Config", Icon: Globe },
    { id: "secrets",    label: "Secrets Scan",           Icon: Key,   module: "secretsScanning" },
    { id: "surface",    label: "Surface Scan",           Icon: Globe, module: "surfaceScan" },
  ].filter(t => !t.module || isModuleAvailable(t.module));
  const currentTab = tabs.some(t => t.id === activeTab) ? activeTab : "monitoring";
  const [orgKey, setOrgKey] = useState(() => {
    const o = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
    return o?.organizationId ?? "none";
  });
  useEffect(() => {
    const sync = () => {
      const o = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
      setOrgKey(o?.organizationId ?? "none");
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);
  const [subscriptions, setSubscriptions] = useState(null);
  const [domain, setDomain] = useState("");
  const [secondaryDomain, setSecondaryDomain] = useState("");
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [addingSecondary, setAddingSecondary] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { message, run } — live-monitoring removal confirm
  const [showDealModal, setShowDealModal] = useState(false);
  const [showBUModal, setShowBUModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [error, setError] = useState(null);
  const [entityDomainInputs, setEntityDomainInputs] = useState({});
  const [addingEntityDomain, setAddingEntityDomain] = useState(null);
  const apiCall = useApi();
  const hasLicense = useHasLicense();
  const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit, canCreateVendor, canManageAnyDeal, canManageAnyBusinessUnit, isSuperAdmin, isSupportAdmin } = useRBAC();

  // Per-scope gates: org primary/secondary + vendor domains need Enterprise Risk Manager;
  // deal/BU domains need management rights on that specific deal/BU.
  const orgId = typeof orgKey === "number" ? orgKey : parseInt(orgKey);
  const canManageOrg = canManageOrganizationRisks(orgId);
  // Super/support admins can view org-level domains read-only (write buttons inside stay
  // gated on canManageOrg). The backend serves org-level subscriptions to them too.
  const canViewOrg = canManageOrg || isSuperAdmin() || isSupportAdmin();
  const canAddDeal = canManageAnyDeal(orgId);
  const canAddBU = canManageAnyBusinessUnit(orgId);
  const canAddVendor = canCreateVendor(orgId);

  const showDeals = hasLicense("Deals");
  const showBusinessUnits = hasLicense("BusinessUnits");
  const showTPRM = hasLicense("TPRM");

  const fetchSubscriptions = async () => {
    try {
      const data = await apiCall("GET", "/enzoic/subscriptions");
      setSubscriptions(data);
      if (data.organization?.primaryDomain) {
        setDomain(data.organization.primaryDomain);
      }
    } catch (err) {}
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchSubscriptions();
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEnrollPrimary = async () => {
    if (!domain.trim()) {
      setError("Please enter a domain name");
      return;
    }
    setEnrolling(true);
    setError(null);
    try {
      await apiCall("POST", "/enzoic/initialize", { domain: domain.trim() });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to enroll domain");
    } finally {
      setEnrolling(false);
    }
  };

  const handleAddSecondary = async () => {
    if (!secondaryDomain.trim()) {
      setError("Please enter a domain name");
      return;
    }
    setAddingSecondary(true);
    setError(null);
    try {
      await apiCall("POST", "/enzoic/initialize-secondary", { domain: secondaryDomain.trim() });
      setSecondaryDomain("");
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to add secondary domain");
    } finally {
      setAddingSecondary(false);
    }
  };

  // Removal actually deletes the dark web monitoring subscription and its findings (unlike
  // integrations/scan targets, it isn't re-created by reconnecting), so it's gated by a custom
  // confirm dialog — a single-step "Are you sure?" (no typed Confirm).
  const performDeleteSubscription = async (subscriptionId) => {
    setDeletingId(subscriptionId);
    setError(null);
    try {
      await apiCall("DELETE", `/enzoic/subscription/${subscriptionId}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to remove subscription");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteSubscription = (subscriptionId, label) =>
    setPendingDelete({
      message: `Are you sure you want to remove monitoring for ${label}? This will delete its dark web monitoring subscription and all associated exposure findings.`,
      run: () => performDeleteSubscription(subscriptionId),
    });

  const performDeletePrimary = async () => {
    setDeletingId("primary");
    setError(null);
    try {
      await apiCall("DELETE", "/enzoic/subscription/primary");
      setDomain("");
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to remove primary domain subscription");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeletePrimary = () => {
    const primaryDomain = subscriptions?.organization?.primary?.domain;
    setPendingDelete({
      message: `Are you sure you want to remove monitoring for ${primaryDomain}? This will stop all primary domain monitoring and delete its associated exposure findings.`,
      run: performDeletePrimary,
    });
  };

  const handleAddEntityDomain = async (entityType, entityId) => {
    const key = `${entityType}-${entityId}`;
    const domainValue = (entityDomainInputs[key] || "").trim();
    if (!domainValue) {
      setError("Please enter a domain name");
      return;
    }
    setAddingEntityDomain(key);
    setError(null);
    try {
      await apiCall("POST", `/enzoic/initialize-${entityType}-domain/${entityId}`, { domain: domainValue });
      setEntityDomainInputs(prev => ({ ...prev, [key]: "" }));
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to add domain to ${entityType}`);
    } finally {
      setAddingEntityDomain(null);
    }
  };

  const hasPrimary = subscriptions?.organization?.primary != null;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
          <div className="mb-3">
              <h1 className="text-3xl font-bold text-content-primary">
                  <span className="text-blue-800">Live Monitoring Config </span>
              </h1>
              <p className="mt-2 text-gray-600">
                  <span className="text-blue-800">Monitor your organization's domains for credential exposures and data breaches</span>
              </p>
          </div>

        {/* Tab bar */}
        <div className="flex border-b border-border mb-8">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${
                currentTab === id
                  ? "border-blue-800 text-blue-800 bg-blue-50"
                  : "border-transparent text-content-secondary hover:text-content-primary hover:border-gray-300"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {currentTab === "secrets" && <SecretsTab key={`secrets-${orgKey}`} s={TAB_STYLE.purple} />}
        {currentTab === "surface" && <SurfaceTab key={`surface-${orgKey}`} s={TAB_STYLE.purple} />}

        {currentTab === "monitoring" && (loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-blue-800" />
          </div>
        ) : (
          <>
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {error}
                </p>
              </div>
            )}

            {/* Organization Section — org-level, hidden from deal/BU-scoped users.
                Visible read-only to super/support admins; write buttons inside stay
                gated on canManageOrg. */}
            {canViewOrg && (
            <div className="bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6">
              <h2 className="text-lg font-semibold text-content-primary mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-800" />
                Organization Domains
              </h2>

              {/* Primary Domain */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-content-primary mb-3">Primary Domain</h3>
                {hasPrimary && (
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Enrolled
                      </span>
                      <span className="text-sm text-content-secondary">
                        Currently monitoring <strong>{subscriptions.organization.primary.domain}</strong>
                      </span>
                    </div>
                    {canManageOrg && (
                    <button
                      onClick={handleDeletePrimary}
                      disabled={deletingId === "primary"}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1"
                      title="Remove primary domain subscription"
                    >
                      {deletingId === "primary" ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                    )}
                  </div>
                )}
                {canManageOrg && (
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label htmlFor="primaryDomain" className="block text-sm font-medium text-content-secondary mb-1">
                      Domain Name
                    </label>
                    <input
                      type="text"
                      id="primaryDomain"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      placeholder="example.com"
                      className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                      disabled={enrolling}
                    />
                  </div>
                  <button
                    onClick={handleEnrollPrimary}
                    disabled={enrolling || !domain.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-800 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {enrolling ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        {hasPrimary ? "Updating..." : "Enrolling..."}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        {hasPrimary ? "Update Enrollment" : "Enroll"}
                      </>
                    )}
                  </button>
                </div>
                )}
                <p className="mt-2 text-xs text-content-secondary">
                  The primary domain is linked to your organization record and used for assessment analysis.
                </p>
              </div>

              {/* Secondary Domains */}
              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-medium text-content-primary mb-3">Secondary Domains</h3>
                {subscriptions?.organization?.secondary?.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {subscriptions.organization.secondary.map((sub) => (
                      <div key={sub.enzoicSubscriptionId} className="flex items-center justify-between py-2 px-3 bg-theme-primary rounded-md border border-border">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-content-secondary" />
                          <span className="text-sm text-content-primary">{sub.domain}</span>
                        </div>
                        {canManageOrg && (
                        <button
                          onClick={() => handleDeleteSubscription(sub.enzoicSubscriptionId, sub.domain)}
                          disabled={deletingId === sub.enzoicSubscriptionId}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1"
                          title="Remove subscription"
                        >
                          {deletingId === sub.enzoicSubscriptionId ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-content-secondary mb-4">No secondary domains configured.</p>
                )}
                {canManageOrg && (
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={secondaryDomain}
                      onChange={(e) => setSecondaryDomain(e.target.value)}
                      placeholder="additional-domain.com"
                      className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                      disabled={addingSecondary}
                    />
                  </div>
                  <button
                    onClick={handleAddSecondary}
                    disabled={addingSecondary || !secondaryDomain.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-800 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingSecondary ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        Add Domain
                      </>
                    )}
                  </button>
                </div>
                )}
                <p className="mt-2 text-xs text-content-secondary">
                  Secondary domains are additional domains monitored for exposures but not linked to your organization record.
                </p>
              </div>
            </div>
            )}

            {/* Deals Section */}
            {showDeals && (
              <div className="bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-content-primary flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-800" />
                    Deal Domains
                  </h2>
                  {canAddDeal && (
                  <button
                    onClick={() => setShowDealModal(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-800 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Add Deal
                  </button>
                  )}
                </div>
                {subscriptions?.deals?.length > 0 ? (
                  <div className="space-y-4">
                    {subscriptions.deals.map((dealGroup) => (
                      <div key={dealGroup.dealId} className="bg-theme-primary rounded-md border border-border p-3">
                        <h4 className="text-sm font-medium text-content-primary mb-2">{dealGroup.dealName}</h4>
                        <div className="space-y-2 mb-3">
                          {(dealGroup.domains || []).map((d) => (
                            <div key={d.enzoicSubscriptionId} className="flex items-center justify-between py-1.5 px-2 bg-theme-secondary rounded border border-border">
                              <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-content-secondary" />
                                <span className="text-sm text-content-primary">{d.domain}</span>
                              </div>
                              {(canManageOrg || canManageDeal(dealGroup.dealId)) && (
                              <button
                                onClick={() => handleDeleteSubscription(d.enzoicSubscriptionId, `${dealGroup.dealName} (${d.domain})`)}
                                disabled={deletingId === d.enzoicSubscriptionId}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1"
                                title="Remove subscription"
                              >
                                {deletingId === d.enzoicSubscriptionId ? (
                                  <Loader className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {(canManageOrg || canManageDeal(dealGroup.dealId)) && (
                        <div className="flex gap-2 items-end">
                          <input
                            type="text"
                            value={entityDomainInputs[`deal-${dealGroup.dealId}`] || ""}
                            onChange={(e) => setEntityDomainInputs(prev => ({ ...prev, [`deal-${dealGroup.dealId}`]: e.target.value }))}
                            placeholder="additional-domain.com"
                            className="flex-1 rounded-md border border-border bg-theme-secondary text-content-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5"
                            disabled={addingEntityDomain === `deal-${dealGroup.dealId}`}
                          />
                          <button
                            onClick={() => handleAddEntityDomain("deal", dealGroup.dealId)}
                            disabled={addingEntityDomain === `deal-${dealGroup.dealId}` || !(entityDomainInputs[`deal-${dealGroup.dealId}`] || "").trim()}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-800 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {addingEntityDomain === `deal-${dealGroup.dealId}` ? (
                              <Loader className="h-3 w-3 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                            Add Domain
                          </button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-content-secondary">
                    No deal domains are being monitored.
                  </p>
                )}
              </div>
            )}

            {/* Business Units Section */}
            {showBusinessUnits && (
              <div className="bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-content-primary flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-800" />
                    Business Unit Domains
                  </h2>
                  {canAddBU && (
                  <button
                    onClick={() => setShowBUModal(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-800 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Add Business Unit
                  </button>
                  )}
                </div>
                {subscriptions?.businessUnits?.length > 0 ? (
                  <div className="space-y-4">
                    {subscriptions.businessUnits.map((buGroup) => (
                      <div key={buGroup.businessUnitId} className="bg-theme-primary rounded-md border border-border p-3">
                        <h4 className="text-sm font-medium text-content-primary mb-2">{buGroup.businessUnitName}</h4>
                        <div className="space-y-2 mb-3">
                          {(buGroup.domains || []).map((d) => (
                            <div key={d.enzoicSubscriptionId} className="flex items-center justify-between py-1.5 px-2 bg-theme-secondary rounded border border-border">
                              <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-content-secondary" />
                                <span className="text-sm text-content-primary">{d.domain}</span>
                              </div>
                              {(canManageOrg || canManageBusinessUnit(buGroup.businessUnitId)) && (
                              <button
                                onClick={() => handleDeleteSubscription(d.enzoicSubscriptionId, `${buGroup.businessUnitName} (${d.domain})`)}
                                disabled={deletingId === d.enzoicSubscriptionId}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1"
                                title="Remove subscription"
                              >
                                {deletingId === d.enzoicSubscriptionId ? (
                                  <Loader className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {(canManageOrg || canManageBusinessUnit(buGroup.businessUnitId)) && (
                        <div className="flex gap-2 items-end">
                          <input
                            type="text"
                            value={entityDomainInputs[`business-unit-${buGroup.businessUnitId}`] || ""}
                            onChange={(e) => setEntityDomainInputs(prev => ({ ...prev, [`business-unit-${buGroup.businessUnitId}`]: e.target.value }))}
                            placeholder="additional-domain.com"
                            className="flex-1 rounded-md border border-border bg-theme-secondary text-content-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5"
                            disabled={addingEntityDomain === `business-unit-${buGroup.businessUnitId}`}
                          />
                          <button
                            onClick={() => handleAddEntityDomain("business-unit", buGroup.businessUnitId)}
                            disabled={addingEntityDomain === `business-unit-${buGroup.businessUnitId}` || !(entityDomainInputs[`business-unit-${buGroup.businessUnitId}`] || "").trim()}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-800 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {addingEntityDomain === `business-unit-${buGroup.businessUnitId}` ? (
                              <Loader className="h-3 w-3 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                            Add Domain
                          </button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-content-secondary">
                    No business unit domains are being monitored.
                  </p>
                )}
              </div>
            )}

            {/* Vendors Section */}
            {showTPRM && (
              <div className="bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-content-primary flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-800" />
                    Vendor Domains
                  </h2>
                  {canAddVendor && (
                  <button
                    onClick={() => setShowVendorModal(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-800 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Add Vendor
                  </button>
                  )}
                </div>
                {subscriptions?.vendors?.length > 0 ? (
                  <div className="space-y-4">
                    {subscriptions.vendors.map((vendorGroup) => (
                      <div key={vendorGroup.vendorId} className="bg-theme-primary rounded-md border border-border p-3">
                        <h4 className="text-sm font-medium text-content-primary mb-2">{vendorGroup.vendorName}</h4>
                        <div className="space-y-2 mb-3">
                          {(vendorGroup.domains || []).map((d) => (
                            <div key={d.enzoicSubscriptionId} className="flex items-center justify-between py-1.5 px-2 bg-theme-secondary rounded border border-border">
                              <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-content-secondary" />
                                <span className="text-sm text-content-primary">{d.domain}</span>
                              </div>
                              {canAddVendor && (
                              <button
                                onClick={() => handleDeleteSubscription(d.enzoicSubscriptionId, `${vendorGroup.vendorName} (${d.domain})`)}
                                disabled={deletingId === d.enzoicSubscriptionId}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1"
                                title="Remove subscription"
                              >
                                {deletingId === d.enzoicSubscriptionId ? (
                                  <Loader className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {canAddVendor && (
                        <div className="flex gap-2 items-end">
                          <input
                            type="text"
                            value={entityDomainInputs[`vendor-${vendorGroup.vendorId}`] || ""}
                            onChange={(e) => setEntityDomainInputs(prev => ({ ...prev, [`vendor-${vendorGroup.vendorId}`]: e.target.value }))}
                            placeholder="additional-domain.com"
                            className="flex-1 rounded-md border border-border bg-theme-secondary text-content-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5"
                            disabled={addingEntityDomain === `vendor-${vendorGroup.vendorId}`}
                          />
                          <button
                            onClick={() => handleAddEntityDomain("vendor", vendorGroup.vendorId)}
                            disabled={addingEntityDomain === `vendor-${vendorGroup.vendorId}` || !(entityDomainInputs[`vendor-${vendorGroup.vendorId}`] || "").trim()}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-800 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {addingEntityDomain === `vendor-${vendorGroup.vendorId}` ? (
                              <Loader className="h-3 w-3 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                            Add Domain
                          </button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-content-secondary">
                    No vendor domains are being monitored.
                  </p>
                )}
              </div>
            )}

          </>
        ))}
      </div>

      {/* Modals */}
      <DarkWebEnrollDealModal
        isOpen={showDealModal}
        onClose={() => setShowDealModal(false)}
        onEnrolled={loadData}
        existingSubscribedDealIds={(subscriptions?.deals || []).map(d => d.dealId)}
      />
      <DarkWebEnrollBusinessUnitModal
        isOpen={showBUModal}
        onClose={() => setShowBUModal(false)}
        onEnrolled={loadData}
        existingSubscribedBUIds={(subscriptions?.businessUnits || []).map(bg => bg.businessUnitId)}
      />
      <DarkWebEnrollVendorModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        onEnrolled={loadData}
        existingSubscribedVendorIds={(subscriptions?.vendors || []).map(v => v.vendorId)}
      />
      <ConfirmDeleteModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => { const p = pendingDelete; setPendingDelete(null); p?.run?.(); }}
        requireTyping={false}
        title="Remove Monitoring"
        confirmLabel="Remove"
        message={pendingDelete?.message || ""}
      />
    </div>
  );
};

export default DarkWebDomainConfig;
